import React, { useEffect, useState, useRef } from "react";
import {
  Spinner,
  Button,
  Image,
  Badge,
  Row,
  Col,
  Card,
  Modal,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import apiClient from "utils/apiClient";
import useFormattedDate from "hooks/useFormatDate";
import { useAuth } from "contexts/AuthContext";
import ProfileSummary from "./ProfileSummary";
import SecurityPhotoSection from "./SecurityPhotoSection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SubtleBadge from "components/common/SubtleBadge";

const UserIdentityDocuments = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { formatTime, formatDate, formatDateTime } = useFormattedDate();
  const { user: loggedInUser } = useAuth();

  const [profile, setProfile] = useState(null);
  const [idDocs, setIdDocs] = useState(null);
  const [workDocs, setWorkDocs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState({ id: false, work: false, securityPhoto: false });
  const [preview, setPreview] = useState({
    show: false,
    url: null,
    isImage: true,
  });
  const [confirmVerify, setConfirmVerify] = useState({
    show: false,
    kind: null,
    type: null,
    decision: null,
  });

  // Zoom and drag state refs for preview modal
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const dragging = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });

  // Utility to check if url is an image (basic check by extension)
  const isImage = (url = "") => {
    try {
      const cleanUrl = url.split("?")[0].toLowerCase();
      return (
        cleanUrl.endsWith(".jpg") ||
        cleanUrl.endsWith(".jpeg") ||
        cleanUrl.endsWith(".png") ||
        cleanUrl.endsWith(".webp") ||
        cleanUrl.endsWith(".gif") ||
        cleanUrl.endsWith(".bmp")
      );
    } catch {
      return false;
    }
  };


  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const headers = {
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
          "O-CLIENT-NAME": "OMC",
          "actor-id": userId,
        };

        const profileResponse = await apiClient.get(
          `/profile-service/api/v1/user-profiles/${userId}?refresh=true`,
          { headers }
        );

        console.log(profileResponse.data);

        if (profileResponse.data) {
          setProfile(profileResponse.data);
          setIdDocs(profileResponse.data.userIdentityVerification);
          setWorkDocs(profileResponse.data.userWorkAuthorization);
        }
      } catch (err) {
        console.error("Error fetching documents:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [userId]);

  const handleVerify = async (type) => {
    try {
      setVerifying((prev) => ({ ...prev, [type]: true }));
      const url =
        type === "id"
          ? `/profile-service/api/v1/user-profiles/identity-verification/user/${userId}/verify`
          : `/profile-service/api/v1/user-profiles/work-authorization/user/${userId}/verify`;

      const response = await apiClient.put(url);
      if (type === "id") setIdDocs(response.data);
      else setWorkDocs(response.data);
      alert("Verification successful!");
    } catch (err) {
      const apiMessage =
        err?.response?.data?.message || err?.response?.data?.error || null;

      if (apiMessage) {
        alert(apiMessage); 
      } else {
        alert("Failed to verify document. Please try again.");
      }
      console.error(err);
    } finally {
      setVerifying((prev) => ({ ...prev, [type]: false }));
    }
  };

  const openVerifyDialog = (type) => {
    setConfirmVerify({ show: true, kind: "document", type, decision: null });
  };

  const closeVerifyDialog = () => {
    setConfirmVerify({ show: false, kind: null, type: null, decision: null });
  };

  const confirmVerification = async () => {
    const { kind, type, decision } = confirmVerify;
    closeVerifyDialog();
    if (kind === "document" && type) {
      await handleVerify(type);
    }
    if (kind === "securityPhoto" && decision) {
      await handleVerifySecurityPhoto(decision);
    }
  };

  const openSecurityPhotoVerifyDialog = (decision = "APPROVED") => {
    setConfirmVerify({
      show: true,
      kind: "securityPhoto",
      type: null,
      decision,
    });
  };

  const handleVerifySecurityPhoto = async (decision = "APPROVED") => {
    try {
      setVerifying((prev) => ({ ...prev, securityPhoto: true }));
      const url = `/profile-service/api/v1/user-profiles/security-photo/user/${userId}/verify?decision=${decision}`;
      const response = await apiClient.put(url);
      setProfile((prev) => ({ 
        ...prev, 
        isSecurityPhotoVerified: decision === "APPROVED",
        securityPhotoVerification: response.data.securityPhotoVerification
      }));
      alert(decision === "APPROVED" ? "Security photo approved!" : "Security photo rejected. User will need to upload a new photo.");
    } catch (err) {
      alert("Failed to verify security photo.");
      console.error(err);
    } finally {
      setVerifying((prev) => ({ ...prev, securityPhoto: false }));
    }
  };

  const handlePreviewClick = (url) => {
    setPreview({ show: true, url, isImage: isImage(url) });
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleClosePreview = () => {
    setPreview({ show: false, url: null, isImage: true });
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  // Drag handlers for zoomed image
  const onMouseDown = (e) => {
    if (zoom === 1) return;
    dragging.current = true;
    lastPos.current = { x: e.clientX, y: e.clientY };
    e.preventDefault();
  };

  const onMouseUp = () => {
    dragging.current = false;
  };

  const onMouseMove = (e) => {
    if (!dragging.current) return;
    e.preventDefault();
    const dx = e.clientX - lastPos.current.x;
    const dy = e.clientY - lastPos.current.y;
    lastPos.current = { x: e.clientX, y: e.clientY };
    setPosition((pos) => ({
      x: pos.x + dx,
      y: pos.y + dy,
    }));
  };

  const toggleZoom = () => {
    setZoom((z) => (z === 1 ? 1.8 : 1));
    if (zoom !== 1) setPosition({ x: 0, y: 0 });
  };


  const DocumentSection = ({ title, docs, type }) => {
    if (!docs) {
      return (
        <Card className="custom-shadow border-0 mb-3">
          <Card.Body>
            <h5 className="fw-semibold mb-3 text-sm">{title}</h5>
            <p className="text-muted mb-0 text-xs">No documents found.</p>
          </Card.Body>
        </Card>
      );
    }

    const isSameUser = loggedInUser?.id === userId;
    const documentType =
      type === "id"
        ? docs.identificationDocumentType
        : docs.workAuthorizationType;

    const documentUrls =
      type === "id" ? docs.idDocumentUrls ?? [] : docs.idDocumentIds ?? [];

    return (
      <Card className="custom-shadow border-0 mb-4">
        <Card.Body>
          <h5 className="fw-semibold mb-3 text-sm">{title}</h5>
          <p className="text-xs mb-1">
            <strong>Type:</strong> {documentType}
          </p>
          <p className="text-xs mb-1">
            <strong>Uploaded Date:</strong> {formatDate(docs.uploadedOn)}
          </p>
          <p className="text-xs mb-1">
            <strong>Uploaded Time:</strong> {formatTime(docs.uploadedOn)}
          </p>
          <p className="text-xs mb-1">
            <strong>Verified:</strong>{" "}
            {docs.verified ? (
              <Badge bg="success">Yes</Badge>
            ) : (
              <Badge bg="warning">No</Badge>
            )}
          </p>
          {docs.verifiedOn && (
            <p className="text-xs mb-1">
              <strong>Verified On:</strong> {formatDateTime(docs.verifiedOn)}
            </p>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: "12px",
              marginTop: "12px",
              marginBottom: "12px",
            }}
          >
            {documentUrls.map((url, i) => (
              <div
                key={i}
                style={{
                  height: 170,
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  background: "#fafafa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  overflow: "hidden",
                  position: "relative",
                }}
                onClick={() => handlePreviewClick(url)}
              >
                {isImage(url) ? (
                  <>
                    <img
                      src={url}
                      alt={`Document ${i + 1}`}
                      draggable={false}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        filter: "blur(2px)",
                        transform: "scale(1.05)",
                        transition: "filter 0.2s ease",
                      }}
                    />

                    {/* overlay */}
                    <div
                      className="doc-overlay"
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: "rgba(0,0,0,0.4)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: "12px",
                        fontWeight: 600,
                        textAlign: "center",
                        padding: "0 10px",
                        pointerEvents: "none",
                        transition: "background 0.2s ease",
                      }}
                    >
                      Click to View
                    </div>
                  </>
                ) : (
                  <div className="text-center text-xs text-muted">
                    <div className="mb-1">
                      <FontAwesomeIcon icon="file-pdf" size="4x" />
                    </div>
                    View document
                  </div>
                )}
              </div>
            ))}
          </div>

          <Button
            variant="success"
            onClick={() => openVerifyDialog(type)}
            disabled={verifying[type] || docs.verified || isSameUser}
            title={
              isSameUser ? "You cannot verify your own documents" : undefined
            }
            className="btn-sm text-xs"
          >
            {verifying[type] ? (
              <>
                <Spinner animation="border" size="sm" /> Verifying...
              </>
            ) : (
              "Verify Document"
            )}
          </Button>
        </Card.Body>
      </Card>
    );
  };


  const BarcodeComparison = ({ comparisons = [] }) => {
     if (!Array.isArray(comparisons) || comparisons.length === 0) {
       return null;
     }

    return (
      <Card className="custom-shadow border-0 mb-4">
        <Card.Header className="bg-white">
          <h6 className="fw-semibold mb-0">Profile vs ID Barcode</h6>
        </Card.Header>

        <table className="table table-borderless mb-0 text-xxs">
          <thead>
            <tr>
              <th>Field</th>
              <th>Profile Value</th>
              <th>ID Barcode Value</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {comparisons.map((item, i) => (
              <tr
                key={i}
                className={i % 2 === 0 ? "bg-light" : ""}
                style={{ verticalAlign: "middle" }}
              >
                <td className="fw-semibold">{item.field}</td>
                <td>{item.profileValue}</td>
                <td>{item.barcodeValue}</td>
                <td>
                  <SubtleBadge bg={item.status === "MATCH" ? "success" : "danger"}>
                    {item.status}
                  </SubtleBadge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    );
  };



  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <>
      <Card
        className="mb-3 rounded border-1 shadow-none"
        style={{
          padding: "0.75rem 1rem",
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div style={{ minWidth: 90 }}>
            <button
              type="button"
              className="btn btn-link p-0 text-black d-flex align-items-center gap-1 text-xs"
              onClick={() => navigate(`/olitask/users/${userId}`)}
              style={{ textDecoration: "none" }}
              aria-label="Go back"
            >
              <span style={{ fontSize: "1.3em", lineHeight: 1 }}>←</span> Back
            </button>
          </div>

          <div className="text-center flex-grow-1 px-3">
            <h4 className="mb-0 fw-semibold text-base">
              User Identity & Work Authorization
            </h4>
          </div>

          <div style={{ minWidth: 90 }} />
        </div>
      </Card>

      <Row>
        <Col lg={4}>
          <ProfileSummary
            profile={profile}
            handlePreviewClick={handlePreviewClick}
          />
        </Col>
        <Col lg={8} className="mt-4 mt-lg-0">
          <SecurityPhotoSection
            photoUrl={profile.securityPhoto}
            verified={profile?.isSecurityPhotoVerified}
            verificationDecision={profile?.securityPhotoVerification?.verificationDecision}
            verifiedOn={profile?.securityPhotoVerification?.verifiedOn || profile?.securityPhotoVerifiedOn}
            uploadedOn={profile?.securityPhotoAt}
            uploadedBy={profile?.securityPhotoVerification?.uploadedBy}
            handlePreviewClick={handlePreviewClick}
            onVerify={openSecurityPhotoVerifyDialog}
            verifying={verifying.securityPhoto}
            isSameUser={loggedInUser?.id === userId}
          />

          <DocumentSection
            title="Identification Documents"
            docs={idDocs}
            type="id"
          />

          <BarcodeComparison comparisons={idDocs?.profileBarcodeComparison} />

          <DocumentSection
            title="Work Authorization Documents"
            docs={workDocs}
            type="work"
          />
        </Col>
      </Row>

      {/* Preview Modal */}
      <Modal
        show={preview.show}
        onHide={handleClosePreview}
        centered
        backdrop="static"
        fullscreen
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-sm">Document Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            height: "80vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
            backgroundColor: "#000",
            cursor: zoom > 1 ? "grab" : "zoom-in",
            userSelect: "none",
          }}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onMouseMove={onMouseMove}
          onClick={toggleZoom}
        >
          {preview.isImage ? (
            <img
              src={preview.url}
              alt="Preview"
              style={{
                maxHeight: "100%",
                maxWidth: "100%",
                transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
                transformOrigin: "center center",
                transition: dragging.current ? "none" : "transform 0.3s ease",
                cursor: zoom > 1 ? "grab" : "zoom-in",
                userSelect: "none",
                borderRadius: 4,
                boxShadow: "0 0 10px rgba(0,0,0,0.3)",
                backgroundColor: "#fff",
              }}
              draggable={false}
            />
          ) : (
            <iframe
              src={preview.url}
              title="Document Preview"
              style={{ width: "100%", height: "80vh", border: "none" }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <small style={{ color: "#ccc", userSelect: "none" }}>
            Click image to zoom in/out. Drag when zoomed.
          </small>
        </Modal.Footer>
      </Modal>

      <Modal show={confirmVerify.show} onHide={closeVerifyDialog} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-sm">Confirm Verification</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-sm">
          {confirmVerify.kind === "securityPhoto"
            ? `Are you sure you want to ${
                confirmVerify.decision === "REJECTED" ? "reject" : "approve"
              } this security photo?`
            : "Are you sure you want to verify this document? This action will mark the document as verified."}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeVerifyDialog}>
            Cancel
          </Button>
          <Button variant="success" onClick={confirmVerification}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UserIdentityDocuments;
